/**
 * Horizon overrides for Shopify.actions plus SongValley Media Session V3.
 */
function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer?.open) drawer.open();
      else window.location.href = Theme.routes.cart_url || '/cart';
    },
  });
}

if (window.Shopify?.actions) init();
else document.addEventListener('DOMContentLoaded', init, { once: true });

(function songValleyMediaSessionV3() {
  if (!('mediaSession' in navigator) || typeof MediaMetadata === 'undefined') return;
  if (window.__songValleyMediaSessionV3Installed) return;
  window.__songValleyMediaSessionV3Installed = true;

  const clean = (value) => String(value ?? '').trim();
  const norm = (value) => clean(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const absolute = (value) => {
    const text = clean(value);
    if (!text) return '';
    try { return new URL(text, document.baseURI).href; } catch (_) { return text; }
  };
  const comparable = (value) => absolute(value).split('#')[0].split('?')[0];
  const isGeneric = (value) => {
    const text = norm(value);
    return !text || text === 'songvalley' || text === 'song valley' || text === 'untitled song' || text === 'unknown artist';
  };

  let activeTrack = null;
  let activeAudio = null;
  let lastSignature = '';

  function playerObject() {
    return window.SongValleyPlayer || window.songValleyPlayer || window.SVPlayer || null;
  }

  function audioElement(player) {
    const p = player || playerObject() || {};
    return p.audio || p.audioElement || document.querySelector('audio[data-sv-audio], audio');
  }

  function catalogSongs() {
    try {
      const node = document.getElementById('sv-experience-data');
      const parsed = node ? JSON.parse(node.textContent || '{}') : {};
      if (Array.isArray(parsed.songs)) return parsed.songs;
    } catch (_) {}
    return Array.isArray(window.SongValleyCatalog) ? window.SongValleyCatalog : [];
  }

  function valueText(value) {
    if (Array.isArray(value)) return value.map(valueText).filter(Boolean).join(', ');
    if (value && typeof value === 'object') {
      return clean(value.name || value.title || value.displayName || value.artistName || value.value);
    }
    return clean(value);
  }

  function artistText(track) {
    const primary = valueText(track?.artist || track?.primaryArtist || track?.artistName);
    const additional = valueText(track?.additionalArtists || track?.additionalPrimaryArtists);
    const featured = valueText(track?.featuredArtists || track?.features);
    const parts = [primary, additional, featured ? `feat. ${featured}` : ''].filter(Boolean);
    return parts.join(', ');
  }

  function albumText(track) {
    return valueText(track?.project || track?.album || track?.projectTitle || track?.albumTitle || track?.releaseTitle);
  }

  function artworkValue(track) {
    return absolute(track?.cover || track?.artwork || track?.coverArt || track?.image || track?.artistImage || '');
  }

  function audioValue(track) {
    return comparable(track?.audioUrl || track?.audio || track?.src || track?.file || '');
  }

  function findCatalogMatch(track) {
    const songs = catalogSongs();
    if (!songs.length) return null;
    const source = audioValue(track) || comparable(activeAudio?.currentSrc || activeAudio?.src || '');
    if (source) {
      const byAudio = songs.find((song) => audioValue(song) === source);
      if (byAudio) return byAudio;
    }
    const title = norm(track?.title || track?.name);
    if (title && !isGeneric(title)) {
      return songs.find((song) => norm(song.title || song.name) === title) || null;
    }
    return null;
  }

  function resolvedMetadata(track) {
    const direct = track && typeof track === 'object' ? track : {};
    const match = findCatalogMatch(direct) || {};

    const directTitle = clean(direct.title || direct.name);
    const matchTitle = clean(match.title || match.name);
    const directArtist = artistText(direct);
    const matchArtist = artistText(match);
    const directAlbum = albumText(direct);
    const matchAlbum = albumText(match);
    const directArtwork = artworkValue(direct);
    const matchArtwork = artworkValue(match);

    return {
      title: isGeneric(directTitle) ? (matchTitle || directTitle || 'SongValley') : directTitle,
      artist: isGeneric(directArtist) ? (matchArtist || directArtist || 'SongValley') : directArtist,
      album: directAlbum || matchAlbum || '',
      artwork: directArtwork || matchArtwork || '',
    };
  }

  function artworkList(source) {
    const src = absolute(source);
    if (!src) return [];
    return [
      { src, sizes: '96x96' },
      { src, sizes: '128x128' },
      { src, sizes: '192x192' },
      { src, sizes: '256x256' },
      { src, sizes: '384x384' },
      { src, sizes: '512x512' },
    ];
  }

  function setPosition() {
    const audio = activeAudio || audioElement();
    if (!audio || typeof navigator.mediaSession.setPositionState !== 'function') return;
    if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
    try {
      navigator.mediaSession.setPositionState({
        duration: audio.duration,
        playbackRate: audio.playbackRate || 1,
        position: Math.min(Math.max(audio.currentTime || 0, 0), audio.duration),
      });
    } catch (_) {}
  }

  function updateMetadata(track, force = false) {
    if (track && typeof track === 'object') activeTrack = track;
    const current = activeTrack || playerObject()?.getCurrentTrack?.() || {};
    const metadata = resolvedMetadata(current);
    const signature = [metadata.title, metadata.artist, metadata.album, metadata.artwork].join('|');
    if (!force && signature === lastSignature) return;
    lastSignature = signature;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: metadata.title,
      artist: metadata.artist,
      album: metadata.album,
      artwork: artworkList(metadata.artwork),
    });
    setPosition();
  }

  function register(action, handler) {
    try { navigator.mediaSession.setActionHandler(action, handler); } catch (_) {}
  }

  register('play', () => {
    const player = playerObject();
    if (typeof player?.play === 'function') player.play();
    else (activeAudio || audioElement())?.play();
  });
  register('pause', () => {
    const player = playerObject();
    if (typeof player?.pause === 'function') player.pause();
    else (activeAudio || audioElement())?.pause();
  });
  register('seekbackward', (details) => {
    const audio = activeAudio || audioElement();
    if (!audio) return;
    audio.currentTime = Math.max(0, audio.currentTime - (details.seekOffset || 10));
    setPosition();
  });
  register('seekforward', (details) => {
    const audio = activeAudio || audioElement();
    if (!audio) return;
    const next = audio.currentTime + (details.seekOffset || 10);
    audio.currentTime = Number.isFinite(audio.duration) ? Math.min(audio.duration, next) : next;
    setPosition();
  });
  register('seekto', (details) => {
    const audio = activeAudio || audioElement();
    if (!audio || typeof details.seekTime !== 'number') return;
    audio.currentTime = details.seekTime;
    setPosition();
  });
  register('previoustrack', () => {
    const player = playerObject();
    if (typeof player?.previous === 'function') player.previous();
    else if (typeof player?.prev === 'function') player.prev();
    else document.querySelector('[data-player-previous], [data-action="previous"], .player-prev, [aria-label*="Previous" i]')?.click();
  });
  register('nexttrack', () => {
    const player = playerObject();
    if (typeof player?.next === 'function') player.next();
    else if (typeof player?.nextTrack === 'function') player.nextTrack();
    else document.querySelector('[data-player-next], [data-action="next"], .player-next, [aria-label*="Next" i]')?.click();
  });

  function bindAudio(audio) {
    if (!(audio instanceof HTMLAudioElement) || audio.dataset.svMediaV3Bound === 'true') return;
    audio.dataset.svMediaV3Bound = 'true';
    activeAudio = audio;

    audio.addEventListener('play', () => {
      activeAudio = audio;
      navigator.mediaSession.playbackState = 'playing';
      updateMetadata(playerObject()?.getCurrentTrack?.() || activeTrack, true);
    });
    audio.addEventListener('pause', () => {
      if (activeAudio === audio) navigator.mediaSession.playbackState = 'paused';
    });
    audio.addEventListener('ended', () => {
      if (activeAudio === audio) navigator.mediaSession.playbackState = 'none';
    });
    ['loadedmetadata', 'durationchange'].forEach((eventName) => {
      audio.addEventListener(eventName, () => {
        updateMetadata(playerObject()?.getCurrentTrack?.() || activeTrack, true);
        setPosition();
      });
    });
    audio.addEventListener('timeupdate', setPosition);
    audio.addEventListener('ratechange', setPosition);
  }

  function syncFromPlayer(player, fallbackTrack) {
    const selected = player?.getCurrentTrack?.() || fallbackTrack || activeTrack;
    if (selected) updateMetadata(selected, true);
    const audio = audioElement(player);
    if (audio) bindAudio(audio);
  }

  function wrapPlayer(player) {
    if (!player || player.__svMediaSessionV3Wrapped) return Boolean(player);
    player.__svMediaSessionV3Wrapped = true;

    if (typeof player.playTrack === 'function') {
      const originalPlayTrack = player.playTrack;
      player.playTrack = function(track) {
        if (track) updateMetadata(track, true);
        const result = originalPlayTrack.apply(this, arguments);
        setTimeout(() => syncFromPlayer(this, track), 0);
        return result;
      };
    }

    if (typeof player.playQueue === 'function') {
      const originalPlayQueue = player.playQueue;
      player.playQueue = function(queue, index) {
        const selected = Array.isArray(queue) ? queue[Number(index) || 0] : null;
        if (selected) updateMetadata(selected, true);
        const result = originalPlayQueue.apply(this, arguments);
        setTimeout(() => syncFromPlayer(this, selected), 0);
        return result;
      };
    }

    syncFromPlayer(player);
    return true;
  }

  document.addEventListener('songvalley:playback-change', (event) => {
    const detail = event.detail || {};
    if (detail.track) updateMetadata(detail.track, true);
    if (typeof detail.playing === 'boolean') {
      navigator.mediaSession.playbackState = detail.playing ? 'playing' : 'paused';
    }
    const audio = audioElement(playerObject());
    if (audio) bindAudio(audio);
  });

  ['songvalley:trackchange', 'songvalley:songchange', 'songvalley:nowplaying', 'sv:trackchange', 'songvalley-player-update'].forEach((name) => {
    document.addEventListener(name, (event) => {
      const track = event.detail?.track || event.detail?.song || playerObject()?.getCurrentTrack?.() || activeTrack;
      if (track) updateMetadata(track, true);
    });
  });

  window.SongValleyMediaSession = {
    refresh: () => updateMetadata(playerObject()?.getCurrentTrack?.() || activeTrack, true),
    update: (track) => updateMetadata(track, true),
  };

  function start() {
    document.querySelectorAll('audio').forEach(bindAudio);
    wrapPlayer(playerObject());

    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      const player = playerObject();
      wrapPlayer(player);
      const audio = audioElement(player);
      if (audio) bindAudio(audio);
      if (player?.getCurrentTrack?.()) updateMetadata(player.getCurrentTrack());
      if (attempts > 240 && player?.__svMediaSessionV3Wrapped) clearInterval(timer);
    }, 500);

    new MutationObserver(() => {
      document.querySelectorAll('audio').forEach(bindAudio);
      wrapPlayer(playerObject());
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
